import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  PaginationItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { get } from "../../../utils/api";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import GovernanceLayout from "../../../Layout/GovernanceLayout";
import { styled } from "@mui/material/styles";
import PaginationBox from "../../../Components/Masters/Pagination";
import Unauthorized from "../../../Components/Unauthorized/Unauthorized";
import AddGovernance from "../AddGovernance";
import AddIcon from "@mui/icons-material/Add";
import NoDataFound from "../../../Components/Masters/NoDataFound";
import MessagePopup from "../../../Components/Masters/MessagePopup";

const GovernaceData = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [pendingData, setPendingData] = useState([]);
  const [completedData, setCompletedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [isShortContent, setIsShortContent] = useState(false);
  const tableContainerRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      const tableHeight = container.scrollHeight;
      const screenHeight = window.innerHeight;
      setIsShortContent(tableHeight < screenHeight / 1.5);
    }
  }, []);

  // Function to fetch data based on type
  const fetchData = async (type) => {
    setIsLoading(true);

    try {
      const response = await get(
        `/governance/getGovernance?type=${type}&page=${page}&limit=${rowsPerPage}`
      );
      console.log(response.data.data,"response.data.data");
      setTotalPages(response.data.totalPages);
      setPage(response.data.currentPage);
      if (type === "Pending") {
        setPendingData(response.data.data || []);
      } else if (type === "Completed") {
        setCompletedData(response.data.data || []);
      } else {
        setPendingData(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data whenever activeTab changes
  useEffect(() => {
    const type = activeTab === 0 ? "Pending" : "Completed";
    fetchData(type, page, rowsPerPage);
  }, [activeTab, page, rowsPerPage]);

  const tableData = activeTab === 0 ? pendingData : completedData;

  const tabStyles = (isActive) => ({
    borderRadius: "4px",
    textTransform: "none",
    backgroundColor: isActive ? "#184D80" : "transparent",
    color: isActive ? "white" : "#184D80",
    borderColor: "#184D80",
    "&:hover": {
      backgroundColor: isActive ? "#163C66" : "rgba(24, 77, 128, 0.1)",
    },
  });

  const handleNavigate = (id, module) => {
    // Determine the first module based on entry type
    let firstModule = "board-structure"; // default for quarterly
    if (module === "annual") {
      firstModule = "executive-compensation";
    }
    navigate(`/governance/data/${firstModule}/${id}`);
  };

  const StyledPaginationItem = styled(PaginationItem)(({ theme }) => ({
    "&.Mui-selected": {
      backgroundColor: "green",
      color: "#fff",
      fontWeight: "bold",
    },
    "&:hover": {
      backgroundColor: "#ededed",
    },
  }));

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // fetchMachinery({ ...filters, page: newPage });
  };

  return (
    <MainDashboardLayout sidebar={<GovernanceLayout />}>
      <Unauthorized requiredModule="Governance">
        <AddGovernance
          handleClose={() => {
            setIsModalOpen(false);
            fetchData("Pending");
          }}
          title="Waste Generated in Operations"
          addData={isModalOpen}
          addUrl={`scope3/wasteGeneratedInOperations/add`}
          updateUrl={`scope3/wasteGeneratedInOperations/update`}
          {...{
            setsnackOpen,
          }}
        />
              <MessagePopup {...{ snackOpen, setsnackOpen }} />{" "}

        <Box sx={{ paddingTop: "20px", width: "100%" }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
            DATA
          </Typography>
          {/* <Typography variant="body2" sx={{ mb: 2 }}>
            To change how frequently you want to create data, navigate to the
            Settings.
          </Typography> */}

          <Box
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <Box style={{ display: "flex", gap: "1rem" }}>
              {["Pending", "Completed"].map((label, index) => (
                <Button
                  key={label}
                  onClick={() => setActiveTab(index)}
                  variant={activeTab === index ? "contained" : "outlined"}
                  sx={tabStyles(activeTab === index)}
                >
                  {label}
                </Button>
              ))}
            </Box>
            <Box>
              <Button
                variant="contained"
                endIcon={<AddIcon />}
                sx={{
                  backgroundColor: "#1c486b",
                  textTransform: "none",
                  fontFamily: "Inter",
                  fontWeight: "500",
                  "&:hover": {
                    backgroundColor: "#1c486b",
                    opacity: 0.9,
                  },
                }}
                onClick={() => setIsModalOpen(true)}
              >
                Add New
              </Button>
            </Box>
          </Box>

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table sx={{ borderCollapse: "collapse" }}>
                <TableHead>
                  <TableRow>
                    {[
                      "S. No",
                      "Module",
                      "Create Date",
                      "Status",
                      "Action",
                    ].map((header) => (
                      <TableCell
                        key={header}
                        align="left"
                        sx={{
                          borderRight: "1px solid #E0E0E0",
                          borderBottom: "1px solid #E0E0E0",
                          fontWeight: "bold",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData.length === 0 ? (
                    <TableRow sx={{ width: "100%" }}>
                      <TableCell
                        colSpan={5}
                        sx={{ textAlign: "center", padding: "20px" }}
                      >
                        <NoDataFound />
                      </TableCell>
                      {/* <NoDataFound /> */}
                    </TableRow>
                  ) : (
                    tableData.map((row, index) => (
                      <TableRow
                        key={row._id} // Ensure unique key, using _id from the response
                        sx={{
                          "& td": {
                            borderRight: "1px solid #E0E0E0",
                            verticalAlign: "middle",
                          },
                          "&:last-child td": { borderBottom: "none" },
                          width: "100%",
                        }}
                      >
                        <TableCell
                          align="left"
                          sx={{
                            fontSize: "14px",
                            // paddingLeft: "2rem",
                            // textAlign:"center",
                            width: "10%",
                            padding: "5px 15px",
                          }}
                        >
                          {index + 1}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{ fontSize: "14px", width: "21%",padding: "5px 15px" }}
                        >
                          {(() => {
                            const rawModule = (row?._governanceDataRef?.module || "").toString();
                            const moduleValue = rawModule.toLowerCase();

                            const year =
                              row?._governanceDataRef?.yearToSubmit !== undefined
                                ? row?._governanceDataRef?.yearToSubmit
                                : row?.year;
                            const quarter =
                              row?._governanceDataRef?.quarterToSubmit !== undefined
                                ? row?._governanceDataRef?.quarterToSubmit
                                : row?.quarter;

                            const yearShort = year
                              ? String(year).slice(-2)
                              : "";

                            const capitalize = (str) =>
                              str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

                            const quarterToId = (value) => {
                              const raw = String(value ?? "").trim().toLowerCase();
                              if (!raw) return null;
                              const asNum = Number(raw);
                              if (asNum >= 1 && asNum <= 4) return asNum;
                              if (raw === "q1" || raw.includes("jan-mar")) return 1;
                              if (raw === "q2" || raw.includes("apr-jun")) return 2;
                              if (raw === "q3" || raw.includes("jul-sep")) return 3;
                              if (raw === "q4" || raw.includes("oct-dec")) return 4;
                              return null;
                            };
                            const qId = quarterToId(quarter);

                            // Annual → "Annual 2026"
                            if (moduleValue === "annual" || moduleValue === "annual module") {
                              const label = capitalize("annual");
                              return year ? `${label} ${year}` : `${label} (-)`;
                            }

                            // Quarterly → "Quarterly Q1-26"
                            if (moduleValue === "quarterly" || moduleValue === "quarterly module") {
                              const label = capitalize("quarterly");
                              if (qId && yearShort) {
                                return `${label} (Q${qId})-${yearShort}`;
                              }
                              if (year) {
                                return `${label} ${year}`;
                              }
                              return `${label} (-)`;
                            }

                            // Fallback
                            if (moduleValue === "quarterly" && yearShort) {
                              // If couldn't parse quarter, still try to render with year
                              return `Quarterly ${year}`;
                            }
                            return rawModule || "-";
                          })()}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{ fontSize: "14px", width: "23%",padding: "5px 15px" }}
                        >
                          {new Date(row.createdAt).toLocaleDateString()}{" "}
                          {/* Show formatted create date */}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            fontSize: "14px",
                            width: "23%",
                            padding: "5px 15px",
                            color:
                              row.status === "Pending"
                                ? "#FF4444"
                                : row.status === "Partially Completed"
                                ? "#FFBB33"
                                : "#00C851",
                          }}
                        >
                          {row.status}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{ fontSize: "14px", width: "23%",padding: "5px" }}
                        >
                          <Button
                            variant="text"
                            sx={{
                              color: "#115293",
                              textTransform: "none",
                              textAlign:"left",
                              fontSize: "14px",
                              "&:hover": { textDecoration: "underline" },
                            }}
                            onClick={() => {
                              const module = row?._governanceDataRef?.module || row?.entryType || "";
                              handleNavigate(row?._governanceDataRef?._id, module);
                            }} // Trigger navigation on click
                          >
                            {row.status === "Pending"
                              ? "Start"
                              : row.status === "Partially Complete"
                              ? "Continue"
                              : "View/Edit"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {totalPages > 1 && (
            <PaginationBox
              totalPages={totalPages}
              page={page}
              handleChangePage={handleChangePage}
              StyledPaginationItem={StyledPaginationItem}
              isShortContent={isShortContent} // Pass this state to PaginationBox
            />
          )}
        </Box>
      </Unauthorized>
    </MainDashboardLayout>
  );
};

export default GovernaceData;
